import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { ArrowRightIcon } from '@heroicons/react/16/solid'
import { useQuery } from '@sequencehq/api'
import {
  Dashboardv20240509Api,
  dashboardv20240509Client
} from '@sequencehq/api/dashboard/v20240509'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import {
  Badge,
  Button,
  SequenceKebabMenu,
  SequenceMenuItem,
  Skeleton
} from '@sequencehq/core-components'
import {
  GreyGrey30,
  GreyGrey50,
  GreyGrey80,
  GreyGrey90,
  Lato12Bold,
  Lato12Regular,
  Lato13Bold,
  Lato13Regular,
  Lato16Bold
} from '@sequencehq/design-tokens'
import {
  toInvoicePaymentStatusBadgeProps,
  toInvoiceStatusBadgeProps,
  toMoney
} from '@sequencehq/utils'
import { format, formatDateRange, parse } from '@sequencehq/utils/dates'
import { useBillingScheduleId } from 'modules/BillingScheduleHome/utils/useBillingScheduleId'
import { Link, useNavigate } from 'react-router-dom'
import { API_DATE_FORMAT } from 'BillingScheduleHome/billingScheduleHome.constants'
import fill from 'lodash/fill'
import { SectionError } from '../common/SectionError'

type InvoiceStatus = DashboardApi20240730.GetInvoice.InvoiceStatus
type InvoicePaymentStatus = DashboardApi20240730.GetInvoice.InvoicePaymentStatus
type APIInvoice = Dashboardv20240509Api.GetInvoices.Invoice

const INVOICE_TABLE_DATE_FORMAT = 'd MMM'

const formatInvoice = (apiInvoice: APIInvoice): TableInvoice => {
  const dueDate = apiInvoice.dueDate
    ? parse(apiInvoice.dueDate, API_DATE_FORMAT, new Date())
    : undefined
  const accountingDate = parse(
    apiInvoice.accountingDate,
    API_DATE_FORMAT,
    new Date()
  )
  const sentDate = apiInvoice.issueDate
    ? parse(apiInvoice.issueDate, API_DATE_FORMAT, new Date())
    : undefined

  const billingPeriod = apiInvoice.billingPeriod
    ? {
        start: parse(
          apiInvoice.billingPeriod.start,
          API_DATE_FORMAT,
          new Date()
        ),
        endInclusive: parse(
          apiInvoice.billingPeriod.endInclusive,
          API_DATE_FORMAT,
          new Date()
        )
      }
    : undefined

  return {
    id: apiInvoice.id,
    amount: toMoney({
      value: apiInvoice.grossTotal,
      currency: apiInvoice.currency
    }),
    currency: apiInvoice.currency,
    status: apiInvoice.status,
    paymentStatus: apiInvoice.paymentStatus,
    invoiceNumber: apiInvoice.invoiceNumber,
    billingPeriod: billingPeriod
      ? formatDateRange({
          from: billingPeriod.start,
          to: billingPeriod.endInclusive
        })
      : '',
    dueDate: dueDate && format(dueDate, INVOICE_TABLE_DATE_FORMAT),
    sentDate: sentDate && format(sentDate, INVOICE_TABLE_DATE_FORMAT),
    date: format(accountingDate, INVOICE_TABLE_DATE_FORMAT)
  }
}

const useInvoicesSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const invoicesQuery = useQuery(
    dashboardv20240509Client.getInvoices,
    {
      billingScheduleId,
      limit: 5
    },
    {
      select: data => {
        if (!data) {
          return {
            invoices: []
          }
        }

        return {
          invoices: data.items.map(formatInvoice)
        }
      }
    }
  )

  return {
    isLoading: invoicesQuery.isPending,
    isError: invoicesQuery.isError,
    invoices: invoicesQuery.data?.invoices ?? []
  }
}

type TableInvoice = {
  id: string
  amount: string
  currency: string
  status: InvoiceStatus
  paymentStatus: InvoicePaymentStatus
  invoiceNumber?: string
  billingPeriod: string
  dueDate?: string
  sentDate?: string
  date: string
}

const InvoiceKebabMenu = (props: {
  invoiceId: string
}) => {
  return (
    <SequenceKebabMenu
      variant="ghost"
      alignment="bottom-right"
      buttonSize="small"
      items={[
        ({ setIsOpen }) => (
          <Link key="view-invoice" to={`/invoices/${props.invoiceId}`}>
            <SequenceMenuItem
              uuid="view-invoice"
              label="View invoice"
              data-testid={`schedule.invoices.${props.invoiceId}.menuItem.view`}
              onClick={() => setIsOpen?.(false)}
            />
          </Link>
        )
      ]}
    />
  )
}

const InvoicesTable = (props: {
  invoices: Array<TableInvoice>
}) => {
  const navigate = useNavigate()

  return (
    <TableContainer
      borderRadius="lg"
      overflow="hidden"
      border={`1px solid ${GreyGrey30}`}
      width="100%"
      boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.02), 0px 1px 1px 0px rgba(0, 0, 0, 0.05)"
    >
      <Table variant="v2" width="100%">
        <Thead userSelect="none">
          <Tr>
            <Th width="140px" textAlign="right">
              Amount
            </Th>
            <Th width="54px"></Th>
            <Th width="140px"></Th>
            <Th width="100%">Invoice #</Th>
            <Th width="188px" textAlign="right">
              Billing period
            </Th>
            <Th textAlign="right" width="66px">
              Sent
            </Th>
            <Th textAlign="right" width="66px">
              Due
            </Th>
            <Th width="68px">Date</Th>
            <Th width="52px"></Th>
          </Tr>
        </Thead>
        {props.invoices.length ? (
          <Tbody>
            {props.invoices.map(invoice => (
              <Tr
                key={invoice.id}
                onClick={e => {
                  if (e.ctrlKey || e.metaKey) {
                    return window.open(`/invoices/${invoice.id}`, '_blank')
                  }
                  navigate(`/invoices/${invoice.id}`)
                }}
              >
                <Td textAlign="right">{invoice.amount}</Td>
                <Td>{invoice.currency}</Td>
                <Td padding="0 12px !important">
                  <Flex gap={2} alignItems="center">
                    <Badge
                      {...toInvoiceStatusBadgeProps({
                        status: invoice.status,
                        size: 'sm'
                      })}
                    />
                    {invoice.status === 'SENT' && (
                      <Badge
                        {...toInvoicePaymentStatusBadgeProps({
                          status: invoice.paymentStatus,
                          size: 'sm'
                        })}
                      />
                    )}
                  </Flex>
                </Td>
                <Td>
                  {invoice.invoiceNumber ? (
                    <Box as="span" {...Lato12Bold}>
                      {invoice.invoiceNumber}
                    </Box>
                  ) : (
                    <Box as="span" {...Lato12Regular} color={GreyGrey50}>
                      INV-DRAFT
                    </Box>
                  )}
                </Td>
                <Td textAlign="right">{invoice.billingPeriod}</Td>
                <Td textAlign="right">{invoice.sentDate}</Td>
                <Td textAlign="right">{invoice.dueDate}</Td>
                <Td>{invoice.date}</Td>
                <Td padding="0 12px !important">
                  <InvoiceKebabMenu invoiceId={invoice.id} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        ) : (
          <Tbody>
            <Tr>
              <Td colSpan={9} rowSpan={2} pointerEvents="none">
                <Flex
                  padding="16px"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  userSelect="none"
                >
                  <Box {...Lato13Bold} color={GreyGrey90}>
                    No invoices to display
                  </Box>
                  <Box {...Lato13Regular} color={GreyGrey80}>
                    No invoices have been created for this schedule yet.
                  </Box>
                </Flex>
              </Td>
            </Tr>
          </Tbody>
        )}
      </Table>
    </TableContainer>
  )
}

export const ScheduleInvoicesSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const { invoices, isError, isLoading } = useInvoicesSection()

  if (isLoading) {
    return (
      <Box>
        <Box {...Lato16Bold} marginBottom="16px">
          Invoices
        </Box>
        <Flex gap="8px" marginBottom="24px" flexDirection="column">
          {fill(Array(5), null).map((_, idx) => (
            <Skeleton height="36px" width="100%" key={idx} />
          ))}
          <Box marginTop="8px">
            <Skeleton height="32px" width="200px" />
          </Box>
        </Flex>
      </Box>
    )
  }

  if (isError) {
    return (
      <Box>
        <Box {...Lato16Bold} marginBottom="16px">
          Invoices
        </Box>
        <Box
          borderRadius="lg"
          padding="16px"
          overflow="hidden"
          border={`1px solid ${GreyGrey30}`}
        >
          <SectionError
            size="medium"
            text="An error occurred when loading the invoice information"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Flex flexDirection="column" gap="8px">
      <Box {...Lato16Bold} marginBottom="8px">
        Invoices
      </Box>
      <InvoicesTable invoices={invoices} />
      {Boolean(invoices.length) && (
        <Link to={`/invoices?billingScheduleId=${billingScheduleId}`}>
          <Button {...Lato13Bold} variant="ghost">
            <Flex alignItems="center" gap="8px">
              View all invoices
              <ArrowRightIcon color="#5F5CFF" height={16} width={16} />
            </Flex>
          </Button>
        </Link>
      )}
    </Flex>
  )
}
